import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Paneset, Loading, Layout } from '@folio/stripes/components';
import ProductivityReportManager from '../../components/reports/builders/productivity/ProductivityReportManager';
import ResultsPreview from '../../components/reports/results/preview/ResultsPreview';
import SaveNewReportManager from '../../components/reports/management/save/SaveNewReportManager';
import useURLParams from '../../hooks/useURLParams';
import useReportContext from '../../hooks/useReportContext';
import useCubeQuery from '../../hooks/useQuery';

function ProductivityReportsRoute(props) {
  const [query, setQuery] = useURLParams('q', '');
  const hideBuilder = new URLSearchParams(props.location?.search).get('hide_builder') === 'true';
  const [showBuilderPane, setShowBuilderPane] = useState(!hideBuilder);
  const [showSavePane, setShowSavePane] = useState(false);
  const [currentSaveMode, setCurrentSaveMode] = useState('new');
  const [currentQuery, setCurrentQuery] = useState('');
  const [queryOptions, setQueryOptions] = useState({});
  const [executionCount, setExecutionCount] = useState(0);
  const [resultSet, error, isLoading] = useCubeQuery(currentQuery, queryOptions);

  const {
    reportId, reportName, reportDesc,
    isRunMode, isEditMode,
    defaultDisplayMethod: savedDefaultDisplay,
    displayMethods: savedDisplayMethods,
    returnSearch,
    isContextLoading,
  } = useReportContext(props.location);

  const [displayMethod, setDisplayMethod] = useURLParams('dm', savedDefaultDisplay);
  const isReportMode = (isRunMode || isEditMode) && reportId;
  const hasChanges = isReportMode && (isEditMode || executionCount > 1);

  const handleClose = () => {
    if (isReportMode && reportId) {
      props.history.push({
        pathname: `/reports/reports-list/${reportId}/view`,
        search: returnSearch || '',
      });
    } else {
      props.history.push('/reports/report-builder');
    }
  };

  const handleOpenSavePane = (mode) => {
    setCurrentSaveMode(mode);
    setShowBuilderPane(false);
    setShowSavePane(true);
  };

  if (isContextLoading) {
    return (
      <Layout className="centered full" style={{ height: '8rem' }}>
        <Loading size="xlarge" />
      </Layout>
    );
  }

  return (
    <Paneset>
      <ProductivityReportManager
        {...props}
        showBuilderPane={showBuilderPane}
        setShowBuilderPane={setShowBuilderPane}
        handleClose={handleClose}
        setQuery={(newQuery, options = {}) => {
          setQuery(newQuery);
          setCurrentQuery(newQuery);
          setQueryOptions(options);
          setExecutionCount(prev => prev + 1);
        }}
        isLoading={isLoading}
      />

      <ResultsPreview
        {...props}
        showQueryBuilderPane={showBuilderPane}
        setShowQueryBuilderPane={setShowBuilderPane}
        showSavePane={showSavePane}
        setShowSavePane={setShowSavePane}
        handleClose={handleClose}
        query={currentQuery}
        setQuery={setCurrentQuery}
        resultSet={resultSet}
        error={error}
        isLoading={isLoading}
        reportType="productivity"
        displayMethod={displayMethod}
        setDisplayMethod={setDisplayMethod}
        savedDisplayMethods={savedDisplayMethods}
        reportName={reportName}
        reportDesc={reportDesc}
        reportId={reportId}
        isRunMode={isRunMode || false}
        isEditMode={isEditMode || false}
        hasChanges={hasChanges}
        onOpenSavePane={handleOpenSavePane}
        isContextLoading={isContextLoading}
      />

      <SaveNewReportManager
        {...props}
        showQueryBuilderPane={showBuilderPane}
        setShowQueryBuilderPane={setShowBuilderPane}
        showSavePane={showSavePane}
        setShowSavePane={setShowSavePane}
        handleClose={handleClose}
        query={currentQuery}
        reportType="productivity"
        builderType="productivity"
        builderState={query}
        saveMode={currentSaveMode}
        savedReportId={reportId}
        savedReportName={reportName}
        savedReportDesc={reportDesc}
      />
    </Paneset>
  );
}

ProductivityReportsRoute.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.object,
};

export default ProductivityReportsRoute;
